import { useCallback, useEffect, useState } from "react";
import type { TimelineItemDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";
import { Button } from "../ui/button.js";
import { Card } from "../ui/card.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { formatDateTime } from "../../lib/format.js";
import { PhoneCall, StickyNote, Plus } from "lucide-react";

interface Form {
  kind: string;
  summary: string;
}

const EMPTY_FORM: Form = { kind: "call", summary: "" };

export function ActivityTimeline({ customerId }: { customerId: string }) {
  const { push } = useToast();
  const { t } = useI18n();
  const [items, setItems] = useState<TimelineItemDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [logging, setLogging] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.getTimeline(customerId);
      setItems(res.items);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.recordActivity(customerId, form);
      push(t("customers.timeline.loggedToast"));
      setForm(EMPTY_FORM);
      setLogging(false);
      await load();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [customerId, form, load, push, t]);

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">
          {t("customers.timeline.title")}
        </h2>
        <Button size="sm" variant="secondary" onClick={() => setLogging((v) => !v)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t("customers.timeline.logActivity")}
        </Button>
      </div>

      {error ? (
        <p role="alert" className="text-sm text-action-danger">
          {error}
        </p>
      ) : null}

      {items.length === 0 && !logging ? (
        <p className="text-sm text-ink-muted">{t("customers.timeline.empty")}</p>
      ) : (
        <ol className="space-y-3">
          {items.map((item) => (
            <li key={item.id} className="flex gap-3">
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-surface-inset text-ink-muted">
                {item.type === "activity" ? (
                  <PhoneCall className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <StickyNote className="h-4 w-4" aria-hidden="true" />
                )}
              </span>
              <div className="min-w-0">
                <p className="text-sm text-ink">
                  <span className="font-medium">
                    {item.type === "activity"
                      ? t(`status.activity.${item.kind}` as MessageKey)
                      : item.type === "task_completed"
                        ? t("customers.timeline.taskCompleted")
                        : t("customers.timeline.taskScheduled")}
                    {" · "}
                  </span>
                  {item.summary}
                </p>
                <p className="text-xs text-ink-muted">
                  {formatDateTime(item.occurredAt)}
                  {item.actorName ? ` · ${item.actorName}` : ""}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}

      {logging ? (
        <div className="mt-3 space-y-3 border-t border-line pt-3">
          <SelectInput
            label={t("common.fields.type")}
            options={[
              { value: "call", label: t("status.activity.call") },
              { value: "meeting", label: t("status.activity.meeting") },
              { value: "email", label: t("status.activity.email") },
              { value: "site_visit", label: t("status.activity.site_visit") },
              { value: "note", label: t("status.activity.note") },
              { value: "other", label: t("status.activity.other") },
            ]}
            value={form.kind}
            onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
          />
          <TextInput
            label={t("customers.timeline.whatHappened")}
            required
            value={form.summary}
            onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
          />
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={submit}
              disabled={busy || !form.summary.trim()}
            >
              {busy ? t("common.saving") : t("customers.timeline.saveActivity")}
            </Button>
            <Button variant="ghost" onClick={() => setLogging(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
